import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useQueryParams } from './useQueryParams';
import { AmenityFormModal } from '../AmenitiesPage';
import { useAdminApi, errorMessage } from './adminApi';
import { Badge, DataTable, Panel, Spinner, Toolbar, btnDanger, btnPrimary, btnSecondary } from './AdminUI';

function AmenitiesTab({ condo, notify }) {
  const api = useAdminApi();
  const { getToken } = useAuth();
  const [params, setParams] = useQueryParams();
  const [amenities, setAmenities] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/api/amenities/condo/${condo._id}`, { includeInactive: true });
      setAmenities(response.data.amenities || []);
    } catch (err) {
      alert(errorMessage(err, 'Failed to fetch amenities'));
      setAmenities([]);
    }
  }, [api, condo._id]);

  useEffect(() => { load(); }, [load]);

  const linkedId = params.get('amenity');
  useEffect(() => {
    if (!linkedId || !amenities) return;
    const linked = amenities.find((a) => String(a._id) === String(linkedId));
    if (linked) setEditing(linked);
    setParams({ amenity: null }, { replace: true });
  }, [linkedId, amenities, setParams]);

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= amenities.length) return;
    const previous = amenities;
    const next = [...amenities];
    [next[index], next[target]] = [next[target], next[index]];
    setAmenities(next);
    try {
      await Promise.all(next.map((a, i) => api.put(`/api/amenities/${a._id}`, { sortOrder: i })));
    } catch (err) {
      setAmenities(previous);
      alert('Failed to reorder amenity');
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/amenities/${deleting._id}`);
      notify(`${deleting.name} deleted.`);
      setDeleting(null);
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to delete amenity'));
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  if (!amenities) return <Spinner />;

  const columns = [
    {
      key: 'order',
      header: <span className="sr-only">Order</span>,
      render: (a) => {
        const index = amenities.indexOf(a);
        return (
          <span className="flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button type="button" aria-label={`Move ${a.name} up`} disabled={index === 0} onClick={() => move(index, -1)} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronUp className="h-4 w-4" /></button>
            <button type="button" aria-label={`Move ${a.name} down`} disabled={index === amenities.length - 1} onClick={() => move(index, 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronDown className="h-4 w-4" /></button>
          </span>
        );
      }
    },
    { key: 'name', header: 'Name', className: 'font-medium text-foreground' },
    { key: 'hours', header: 'Hours', className: 'whitespace-nowrap', render: (a) => `${a.availableFrom} – ${a.availableTo}` },
    { key: 'slot', header: 'Slot', hideBelow: 'md', render: (a) => `${a.bookingDurationMinutes} min` },
    { key: 'capacity', header: 'Capacity', hideBelow: 'md', render: (a) => a.maxCapacity },
    { key: 'advance', header: 'Book ahead', hideBelow: 'xl', render: (a) => `${a.advanceBookingDays} days` },
    { key: 'status', header: 'Status', render: (a) => (a.isActive ? <Badge className="text-success border-success">Active</Badge> : <Badge>Inactive</Badge>) },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      render: (a) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); setDeleting(a); }} className="text-destructive hover:text-destructive/80 text-sm">Delete</button>
      )
    }
  ];

  return (
    <Panel>
      <Toolbar>
        <h2 className="text-base font-semibold text-foreground mr-auto">Amenities ({amenities.length})</h2>
        <button type="button" onClick={() => setEditing('new')} className={btnPrimary}>+ Add Amenity</button>
      </Toolbar>
      <DataTable label="Amenities" columns={columns} rows={amenities} onRowClick={(a) => setEditing(a)} emptyMessage="No amenities yet." />

      {editing && (
        <AmenityFormModal
          amenity={editing === 'new' ? null : editing}
          condoId={condo._id}
          getToken={getToken}
          onClose={() => setEditing(null)}
          onSave={() => { setEditing(null); notify('Amenity saved.'); load(); }}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="delete-amenity-title">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-sm p-6">
            <h3 id="delete-amenity-title" className="text-lg font-semibold text-foreground mb-2">Delete Amenity</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Delete <span className="font-medium text-foreground">{deleting.name}</span>? This also cancels all its bookings.
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={remove} disabled={busy} className={`${btnDanger} flex-1 justify-center`}>{busy ? 'Deleting...' : 'Delete'}</button>
              <button type="button" onClick={() => setDeleting(null)} disabled={busy} className={`${btnSecondary} flex-1 justify-center`}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

export default AmenitiesTab;
